<template>
  <div class="modern-table-page">
    <!-- Search and Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.type_to_search") }}</label>
        <b-form-input
          v-model="filter"
          class="search-input"
          :placeholder="$t('globals.search_for') + '...'"
        />
      </div>

      <div class="filter-group">
        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.time_range") || "Time Range" }}</label>
          <b-form-select
            v-model="for_days"
            :options="for_days_options"
            class="filter-select"
          />
        </div>
      </div>

      <button class="reload-btn" @click="resetReload" :title="$t('globals.reload')">
        <icon name="sync-alt" :spin="getAllScheduledInvoicesLoading"></icon>
      </button>
    </div>

    <!-- Table -->
    <div class="table-wrapper">
      <table class="modern-table">
        <thead>
          <tr>
            <th
              v-for="field in fields"
              :key="field.key"
              :class="{ sortable: field.sortable }"
              @click="field.sortable && handleSort(field.key)"
            >
              {{ field.label || field.key }}
              <span v-if="sortBy === field.key" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in paginatedItems"
            :key="item.id"
            class="table-row"
          >
            <td>{{ item.id }}</td>
            <td>{{ item.order_id }}</td>
            <td>
              <span class="status-badge" :class="`status-${item.status}`">
                {{ item.status }}
              </span>
            </td>
            <td>{{ item.schedule_at }}</td>
            <td style="text-align: right;">{{ currencyFormat(item.total_price, item.currency) }}</td>
            <td>
              <b-button
                variant="info"
                size="sm"
                @click="goToOrder(item.order_id)"
              >
                {{ $t("globals.order") }}
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="getAllScheduledInvoicesLoading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t("globals.loading") }}...</p>
      </div>
      <div v-else-if="filteredItems.length === 0" class="empty-state">
        <p>{{ $t("globals.no_results") }}</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">{{ $t("globals.show") }}</span>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          class="per-page-select"
        />
        <span class="pagination-info">
          {{ paginationText }}
        </span>
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          &lt;
        </button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-btn"
          :class="{ active: page === currentPage }"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          &gt;
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { currencyFormat } from "@/helpers/price";
import { mapGetters, mapMutations, mapActions } from "vuex";

import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue")
  },
  data() {
    return {
      for_days: 2,
      filter: null,
      currentPage: 1,
      perPage: 20,
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      sortBy: "schedule_at",
      sortDesc: false,
      for_days_options: [
        {
          text: "Next 2 days",
          value: 2
        },
        {
          text: "Next 5 days",
          value: 5
        },
        {
          text: "Next 7 days",
          value: 7
        }
      ],
      fields: [
        {
          key: "id",
          sortable: true
        },
        {
          key: "order_id",
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "schedule_at",
          label: this.$t("globals.schedule_at"),
          sortable: true
        },
        {
          key: "total_price",
          label: this.$t("globals.total_price"),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("globals.actions")
        }
      ]
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions(["fetchAllScheduledInvoices"]),
    currencyFormat,
    handleSort(fieldKey) {
      if (fieldKey === 'actions') return; // Don't sort actions column

      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
    },
    goToOrder(id) {
      this.$router.push({ name: "order", params: { id: id } });
    },
    resetReload() {
      this.filter = null;
      this.currentPage = 1;
      this.fetchAllScheduledInvoices({ days: this.for_days });
    }
  },
  computed: {
    ...mapGetters(["getAllScheduledInvoices", "getAllScheduledInvoicesLoading", "getUserBrandPermissions"]),

    filteredItems() {
      let items = [...this.getAllScheduledInvoices];

      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        items = items.filter(item => {
          const id = item.id?.toString().toLowerCase() || '';
          const orderId = item.order_id?.toString().toLowerCase() || '';
          const status = item.status?.toLowerCase() || '';
          return id.includes(searchTerm) ||
                 orderId.includes(searchTerm) ||
                 status.includes(searchTerm);
        });
      }

      items.sort((a, b) => {
        let aVal = a[this.sortBy] || '';
        let bVal = b[this.sortBy] || '';

        if (aVal < bVal) return this.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.sortDesc ? -1 : 1;
        return 0;
      });

      return items;
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage);
    },

    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    },

    paginationText() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.filteredItems.length);
      const total = this.filteredItems.length;
      return `${start}-${end} of ${total}`;
    }
  },
  watch: {
    for_days: function(newDays) {
      this.fetchAllScheduledInvoices({ days: newDays });
    },
    paginatedItems() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = 1;
      }
    }
  },
  mounted() {
    this.fetchAllScheduledInvoices({ days: this.for_days });
  }
};
</script>

<style scoped>
/* Styles are imported from modern-table.css */
</style>
